import { AudioEffectCompositeBox, AudioEffectCompositeCellBox, AudioFileBox, AutotuneDeviceBox, CompressorDeviceBox, ConvolverDeviceBox, CrusherDeviceBox, DattorroReverbDeviceBox, DelayDeviceBox, FoldDeviceBox, FrequencySplitBox, GateDeviceBox, MaximizerDeviceBox, NeuralAmpDeviceBox, RevampDeviceBox, ReverbDeviceBox, StereoCompositeBox, StereoToolDeviceBox, TidalDeviceBox, VocoderDeviceBox, WaveshaperDeviceBox, WerkstattDeviceBox } from "@opendaw/studio-boxes";
import { IndexedBox } from "@opendaw/lib-box";
import { asInstanceOf, isNull, panic } from "@opendaw/lib-std";
import { Accessors, Facade, Props } from "../Common";
import { EffectChain, EffectFacade } from "./EffectChain";
import { Facades } from "../Facades";
import { ScriptSupport } from "./ScriptDevices";
import { AudioFiles } from "../AudioFiles";
import { DeviceBoxes } from "./DeviceBoxes";
import { Guard } from "../Guard";
export class AudioEffectFacade extends EffectFacade {
    get audioUnit() { return Facades.audioUnitOf(this.context, this.box); }
}
export var SideChains;
(function (SideChains) {
    SideChains.read = (context, pointer) => pointer.targetVertex.mapOr(vertex => Facades.forVertex(context, vertex), null);
    SideChains.write = (context, owner, pointer, source) => context.edit(() => {
        if (isNull(source)) {
            pointer.defer();
            return;
        }
        const box = Facades.boxOf(source);
        if (Facades.isCompositeBox(box) && Facades.isNestedIn(owner, box)) {
            pointer.refer(Facades.compositeInputField(box));
        }
        else {
            pointer.refer(box);
        }
    });
})(SideChains || (SideChains = {}));
export class SideChainableEffect extends AudioEffectFacade {
    get sideChain() { return SideChains.read(this.context, this.box.sideChain); }
    set sideChain(source) { SideChains.write(this.context, this.box, this.box.sideChain, source); }
}
export class AutotuneEffectImpl extends AudioEffectFacade {
    key = "Autotune";
    constructor(context, box) {
        super(context, box);
        this.bind({
            scaleKey: box.key, scale: box.scale, amount: box.amount, retune: box.retune,
            shift: box.shift, smooth: box.smooth
        });
    }
}
export class CompressorEffectImpl extends SideChainableEffect {
    key = "Compressor";
    constructor(context, box) {
        super(context, box);
        this.bind({
            lookahead: box.lookahead, automakeup: box.automakeup, autoattack: box.autoattack,
            autorelease: box.autorelease, inputGain: box.inputgain, threshold: box.threshold, ratio: box.ratio,
            knee: box.knee, attack: box.attack, release: box.release, makeup: box.makeup, mix: box.mix
        });
    }
}
export class ConvolverEffectImpl extends AudioEffectFacade {
    key = "Convolver";
    constructor(context, box) {
        super(context, box);
        this.bind({ wet: box.wet, dry: box.dry, preDelay: box.preDelay, normalize: box.normalize, reverse: box.reverse });
    }
    get impulse() {
        const fileBox = Accessors.pointerBox(this.box.file, AudioFileBox);
        return isNull(fileBox) ? null : AudioFiles.toSample(this.context, fileBox);
    }
    set impulse(sample) {
        AudioFiles.assign(this.context, this.box.file, () => isNull(sample) ? null : AudioFiles.use(this.context, sample));
    }
}
export class CrusherEffectImpl extends AudioEffectFacade {
    key = "Crusher";
    constructor(context, box) {
        super(context, box);
        this.bind({ crush: box.crush, bits: box.bits, boost: box.boost, mix: box.mix });
    }
}
export class DattorroReverbEffectImpl extends AudioEffectFacade {
    key = "DattorroReverb";
    constructor(context, box) {
        super(context, box);
        this.bind({
            preDelay: box.preDelay, bandwidth: box.bandwidth, inputDiffusion1: box.inputDiffusion1,
            inputDiffusion2: box.inputDiffusion2, decay: box.decay, decayDiffusion1: box.decayDiffusion1,
            decayDiffusion2: box.decayDiffusion2, damping: box.damping, excursionRate: box.excursionRate,
            excursionDepth: box.excursionDepth, wet: box.wet, dry: box.dry
        });
    }
}
export class DelayEffectImpl extends AudioEffectFacade {
    key = "Delay";
    constructor(context, box) {
        super(context, box);
        this.bind({
            delay: box.delayMusical, delayMillis: box.delayMillis, preSyncTimeLeft: box.preSyncTimeLeft,
            preMillisTimeLeft: box.preMillisTimeLeft, preSyncTimeRight: box.preSyncTimeRight,
            preMillisTimeRight: box.preMillisTimeRight, feedback: box.feedback, cross: box.cross,
            lfoSpeed: box.lfoSpeed, lfoDepth: box.lfoDepth, filter: box.filter, wet: box.wet, dry: box.dry
        });
    }
}
export class FoldEffectImpl extends AudioEffectFacade {
    key = "Fold";
    constructor(context, box) {
        super(context, box);
        this.bind({ drive: box.drive, overSampling: box.overSampling, volume: box.volume });
    }
}
export class GateEffectImpl extends SideChainableEffect {
    key = "Gate";
    constructor(context, box) {
        super(context, box);
        this.bind({
            threshold: box.threshold, return: box.return, attack: box.attack, hold: box.hold,
            release: box.release, floor: box.floor, inverse: box.inverse
        });
    }
}
export class MaximizerEffectImpl extends AudioEffectFacade {
    key = "Maximizer";
    constructor(context, box) {
        super(context, box);
        this.bind({ lookahead: box.lookahead, threshold: box.threshold });
    }
}
export class NeuralAmpEffectImpl extends AudioEffectFacade {
    key = "NeuralAmp";
    constructor(context, box) {
        super(context, box);
        this.bind({ inputGain: box.inputGain, outputGain: box.outputGain, mono: box.mono, mix: box.mix });
    }
}
export class RevampEffectImpl extends AudioEffectFacade {
    key = "Revamp";
    constructor(context, box) {
        super(context, box);
        const pass = (field) => ({ enabled: field.enabled, frequency: field.frequency, order: field.order, q: field.q });
        const shelf = (field) => ({ enabled: field.enabled, frequency: field.frequency, gain: field.gain });
        const bell = (field) => ({ enabled: field.enabled, frequency: field.frequency, gain: field.gain, q: field.q });
        this.bind({
            highPass: pass(box.highPass), lowShelf: shelf(box.lowShelf), lowBell: bell(box.lowBell),
            midBell: bell(box.midBell), highBell: bell(box.highBell), highShelf: shelf(box.highShelf),
            lowPass: pass(box.lowPass)
        });
    }
}
export class ReverbEffectImpl extends AudioEffectFacade {
    key = "Reverb";
    constructor(context, box) {
        super(context, box);
        this.bind({ decay: box.decay, preDelay: box.preDelay, damp: box.damp, filter: box.filter, wet: box.wet, dry: box.dry });
    }
}
export class StereoToolEffectImpl extends AudioEffectFacade {
    key = "StereoTool";
    constructor(context, box) {
        super(context, box);
        this.bind({
            volume: box.volume, panning: box.panning, stereo: box.stereo, invertL: box.invertL,
            invertR: box.invertR, swap: box.swap, panningMixing: box.panningMixing
        });
    }
}
export class TidalEffectImpl extends AudioEffectFacade {
    key = "Tidal";
    constructor(context, box) {
        super(context, box);
        this.bind({
            slope: box.slope, symmetry: box.symmetry, rate: box.rate, depth: box.depth,
            offset: box.offset, channelOffset: box.channelOffset
        });
    }
}
export class VocoderEffectImpl extends SideChainableEffect {
    key = "Vocoder";
    constructor(context, box) {
        super(context, box);
        this.bind({
            carrierMinFreq: box.carrierMinFreq, carrierMaxFreq: box.carrierMaxFreq,
            modulatorMinFreq: box.modulatorMinFreq, modulatorMaxFreq: box.modulatorMaxFreq,
            qStart: box.qStart, qEnd: box.qEnd, envAttack: box.envAttack, envRelease: box.envRelease,
            gain: box.gain, mix: box.mix, bandCount: box.bandCount
        });
    }
    get modulatorSource() {
        return this.box.modulatorSource.getValue();
    }
    set modulatorSource(value) {
        const validated = Guard.oneOf(value, ["noise-pink", "noise-white", "input"], "modulatorSource");
        this.context.edit(() => this.box.modulatorSource.setValue(validated));
    }
}
export class WaveshaperEffectImpl extends AudioEffectFacade {
    key = "Waveshaper";
    constructor(context, box) {
        super(context, box);
        this.bind({ equation: box.equation, inputGain: box.inputGain, outputGain: box.outputGain, mix: box.mix });
    }
}
export class WerkstattEffectImpl extends AudioEffectFacade {
    key = "Werkstatt";
    #script;
    constructor(context, box) {
        super(context, box);
        this.#script = new ScriptSupport(context, box, "werkstatt");
    }
    get code() { return this.#script.code; }
    set code(source) { this.#script.code = source; }
    get parameters() { return this.#script.parameters; }
    get samples() { return this.#script.samples; }
    parameter(label) { return this.#script.parameter(label); }
    sample(label) { return this.#script.sample(label); }
}
export class AudioEffectCompositeEntryImpl extends Facade {
    static wrap(context, box) {
        return context.facade(box, () => new AudioEffectCompositeEntryImpl(context, box));
    }
    #chain;
    constructor(context, box) {
        super(context, box);
        this.bind({ label: box.label, gain: box.gain, mute: box.mute, solo: box.solo, pan: box.pan });
        this.#chain = new EffectChain(context, box.audioEffects, box => AudioEffectImpls.wrap(context, box));
    }
    get index() { return this.box.index.getValue(); }
    get composite() {
        const compositeBox = this.box.composite.targetVertex.unwrap("entry has no composite").box;
        return AudioEffectImpls.wrap(this.context, compositeBox);
    }
    get audioEffects() { return this.#chain.list(); }
    addAudioEffect(key, props, index) {
        Guard.oneOf(key, Object.keys(DeviceBoxes.AudioEffectLabels), "key");
        return this.#chain.add(at => DeviceBoxes.createAudioEffect(this.context.boxGraph, key, this.box.audioEffects, at), props, index);
    }
    remove() {
        const compositeBox = this.box.composite.targetVertex.unwrap("entry has no composite").box;
        if (!(compositeBox instanceof AudioEffectCompositeBox)) {
            return panic(`Entries of ${compositeBox.name} are fixed and cannot be removed`);
        }
        this.context.edit(() => {
            const index = this.index;
            IndexedBox.removeOrder(compositeBox.entries, index);
            this.box.delete();
        });
    }
}
const listEntries = (context, field) => IndexedBox.collectIndexedBoxes(field)
    .map(box => AudioEffectCompositeEntryImpl.wrap(context, asInstanceOf(box, AudioEffectCompositeCellBox)));
export class AudioEffectCompositeImpl extends AudioEffectFacade {
    key = "Composite";
    constructor(context, box) {
        super(context, box);
        this.bind({ dry: box.dry, wet: box.wet });
    }
    get entries() { return listEntries(this.context, this.box.entries); }
    addEntry(props) {
        return this.context.edit(() => {
            const index = this.box.entries.pointerHub.incoming().length;
            const box = DeviceBoxes.createCompositeEntry(this.context.boxGraph, this.box.entries, index, `Entry ${index + 1}`);
            return Props.apply(AudioEffectCompositeEntryImpl.wrap(this.context, box), props);
        });
    }
}
export class StereoSplitEffectImpl extends AudioEffectFacade {
    key = "StereoSplit";
    constructor(context, box) {
        super(context, box);
        this.bind({ dry: box.dry, wet: box.wet });
    }
    get entries() { return listEntries(this.context, this.box.entries); }
}
export class FrequencySplitEffectImpl extends AudioEffectFacade {
    key = "FrequencySplit";
    constructor(context, box) {
        super(context, box);
        this.bind({ dry: box.dry, wet: box.wet, crossover1: box.crossover1, crossover2: box.crossover2, crossover3: box.crossover3 });
    }
    get entries() { return listEntries(this.context, this.box.entries); }
}
export var AudioEffectImpls;
(function (AudioEffectImpls) {
    AudioEffectImpls.wrap = (context, box) => context.facade(box, () => {
        if (box instanceof AutotuneDeviceBox) {
            return new AutotuneEffectImpl(context, box);
        }
        if (box instanceof CompressorDeviceBox) {
            return new CompressorEffectImpl(context, box);
        }
        if (box instanceof ConvolverDeviceBox) {
            return new ConvolverEffectImpl(context, box);
        }
        if (box instanceof CrusherDeviceBox) {
            return new CrusherEffectImpl(context, box);
        }
        if (box instanceof DattorroReverbDeviceBox) {
            return new DattorroReverbEffectImpl(context, box);
        }
        if (box instanceof DelayDeviceBox) {
            return new DelayEffectImpl(context, box);
        }
        if (box instanceof FoldDeviceBox) {
            return new FoldEffectImpl(context, box);
        }
        if (box instanceof GateDeviceBox) {
            return new GateEffectImpl(context, box);
        }
        if (box instanceof MaximizerDeviceBox) {
            return new MaximizerEffectImpl(context, box);
        }
        if (box instanceof NeuralAmpDeviceBox) {
            return new NeuralAmpEffectImpl(context, box);
        }
        if (box instanceof RevampDeviceBox) {
            return new RevampEffectImpl(context, box);
        }
        if (box instanceof ReverbDeviceBox) {
            return new ReverbEffectImpl(context, box);
        }
        if (box instanceof StereoToolDeviceBox) {
            return new StereoToolEffectImpl(context, box);
        }
        if (box instanceof TidalDeviceBox) {
            return new TidalEffectImpl(context, box);
        }
        if (box instanceof VocoderDeviceBox) {
            return new VocoderEffectImpl(context, box);
        }
        if (box instanceof WaveshaperDeviceBox) {
            return new WaveshaperEffectImpl(context, box);
        }
        if (box instanceof WerkstattDeviceBox) {
            return new WerkstattEffectImpl(context, box);
        }
        if (box instanceof AudioEffectCompositeBox) {
            return new AudioEffectCompositeImpl(context, box);
        }
        if (box instanceof StereoCompositeBox) {
            return new StereoSplitEffectImpl(context, box);
        }
        if (box instanceof FrequencySplitBox) {
            return new FrequencySplitEffectImpl(context, box);
        }
        return panic(`${box.name} is not a supported audio-effect`);
    });
    AudioEffectImpls.isBox = (box) => DeviceBoxes.isAudioEffectBox(box.name);
    AudioEffectImpls.isCompositeBox = (box) => box instanceof AudioEffectCompositeBox || box instanceof StereoCompositeBox || box instanceof FrequencySplitBox;
})(AudioEffectImpls || (AudioEffectImpls = {}));
